import { useEffect, useState } from "react";
import {
  Alert,
  AlertTitle,
  Button,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Loading } from "@lopie/kernel-ui";

import { api } from "../lib/oncall-api.ts";

/**
 * You, and the one question this screen exists to answer: why did it not
 * reach me?
 *
 * The system this replaces had a phone number per person, typed in here, in
 * this app's own table — drifting from the directory that already held it
 * from the moment it was typed. This screen is not that, and the difference
 * is worth being precise about, because it looks identical from the outside.
 *
 * The number a page dials comes from your organisation's directory. When the
 * directory is wrong — a number replaced two years ago, and nobody at that
 * company is going to fix it today — you can state the right one here, and
 * what you are stating is a *kernel record*, not a copy this app keeps:
 *
 * - every app that can see you sees the corrected number
 * - it says who set it and when
 * - the next directory sync cannot quietly revert it
 * - one button hands it back to the directory
 *
 * So the fix takes effect on the next page rather than on the next sync, and
 * there is still only one number rather than two that disagree.
 */
export default function Me() {
  const [me, setMe] = useState(null);
  const [failure, setFailure] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  const load = () => api.me().then(setMe).catch((e) => setFailure(e.message));

  useEffect(() => { load(); }, []);

  const act = async (work) => {
    setBusy(true);
    setProblem(null);
    try {
      await work();
      setDraft("");
      await load();
    } catch (e) {
      // Shown, not swallowed. A correction that silently did nothing leaves
      // somebody believing they are reachable.
      setProblem(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (failure) {
    return (
      <Alert severity="error" variant="outlined">
        <AlertTitle>Could not read your details</AlertTitle>
        {failure}
      </Alert>
    );
  }
  if (!me) return <Loading label="Reading your details" />;

  if (!me.knownToTheDirectory) {
    // Not `EmptyState`. This is not zero rows from a request that worked —
    // it is a person who cannot be paged and cannot fix it themselves, which
    // is the amber that says look at this.
    return (
      <Alert severity="warning" variant="outlined">
        <AlertTitle>We cannot find you in the directory</AlertTitle>
        Nobody in your organisation's directory has the address you sign in
        with on their record, so this pager cannot tell which person you are.
        It cannot ring you, and you cannot be put on a rotation. Someone who
        administers the directory can add the address to your record.
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      {me.pageable ? (
        <Alert severity="success" variant="outlined">
          <AlertTitle>A page would reach you</AlertTitle>
          A text goes to the first number below, and a call follows if you do
          not acknowledge.
        </Alert>
      ) : (
        <Alert severity="warning" variant="outlined">
          <AlertTitle>A page would not reach you</AlertTitle>
          There is no phone number for you, so being on a rotation would mean
          an incident escalating past you in silence. Set one below.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
          Numbers a page would use
        </Typography>
        {me.phones?.length ? (
          <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", mt: 0.5 }}>
            {me.phones.map((phone, n) => (
              <Chip
                key={phone}
                size="small"
                variant="outlined"
                // Colour is never the only signal: the label says "set by
                // you" in words, and the chip below repeats it in prose.
                color={me.numberIsHeld ? "primary" : "default"}
                label={n === 0 ? `${phone} · tried first` : phone}
                sx={{ fontFamily: "monospace" }}
              />
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">None.</Typography>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {me.numberComesFrom}
          {me.numberSetAt ? ` Set ${new Date(me.numberSetAt).toLocaleString()}.` : ""}
        </Typography>
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
          {me.numberIsHeld ? "Change the number" : "Use a different number"}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {me.numberIsHeld
            ? "This number is one you set. It stays through every directory "
              + "sync until you hand it back."
            : "If the directory has the wrong number, set the right one here. "
              + "It takes effect on the next page, not the next sync — and it "
              + "is recorded against your record rather than kept privately by "
              + "this app."}
        </Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ mt: 2, alignItems: { sm: "flex-start" } }}
        >
          <TextField
            size="small"
            label="Mobile number"
            placeholder="+1 619 555 0000"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={busy}
            helperText="Include the country code."
            sx={{ minWidth: { sm: 260 } }}
          />
          <Button
            variant="contained"
            disabled={busy || !draft.trim()}
            onClick={() => act(() => api.setPhone(draft.trim()))}
          >
            Use this number
          </Button>
          {me.numberIsHeld ? (
            <Button
              variant="outlined"
              disabled={busy}
              onClick={() => act(() => api.clearPhone())}
            >
              Use the directory's again
            </Button>
          ) : null}
        </Stack>

        {problem ? (
          <Alert severity="error" variant="outlined" sx={{ mt: 2 }}>
            <AlertTitle>That did not save</AlertTitle>
            {problem}
          </Alert>
        ) : null}
      </Paper>
    </Stack>
  );
}
